<template>
  <div class="stall-cart">
    <!-- 顶部导航 -->
    <header class="header">
      <button class="btn-icon" @click="$router.push('/stall/home')">←</button>
      <div class="header-center">🛒 {{ $t('cart.title') }}</div>
      <div style="width: 24px;"></div>
    </header>

    <!-- 内容 -->
    <div class="content">
      <!-- 批发价包信息 -->
      <div v-if="packageInfo" class="package-info-bar">
        <div class="package-name">💰 {{ packageInfo.name }}</div>
        <div v-if="packageInfo.validDate" class="package-valid">📅 {{ packageInfo.validDate }}</div>
        <button class="btn-exit-package" @click="exitPackageMode">✕ {{ $t('common.exit') }}</button>
      </div>

      <!-- 加载中 -->
      <div v-if="packageLoading" class="loading-cart">
        <div class="spinner">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <!-- 空购物车 -->
      <div v-else-if="cartItems.length === 0" class="empty-cart">
        <div class="empty-icon">🛒</div>
        <p>{{ $t('cart.empty') }}</p>
        <button class="btn-go-shopping" @click="$router.push('/stall/products')">
          {{ $t('cart.goShopping') }}
        </button>
      </div>

      <!-- 购物车商品列表 -->
      <div v-else>
        <!-- 批量操作栏 -->
        <div class="batch-actions">
          <label class="select-all-label">
            <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" />
            <span>{{ $t('cart.selectAll') }}</span>
          </label>
          <div class="batch-buttons">
            <button class="btn-delete-batch" @click="deleteSelected" :disabled="selectedIds.length === 0">
              {{ $t('cart.remove') }} ({{ selectedIds.length }})
            </button>
            <button class="btn-clear-cart" @click="clearCart">
              {{ $t('cart.clearCart') }}
            </button>
          </div>
        </div>

        <!-- 商品列表 -->
        <div v-for="item in cartItems" :key="item.id" class="cart-item">
          <div class="item-checkbox">
            <input type="checkbox" v-model="selectedIds" :value="item.id" />
          </div>
          <div class="item-info">
            <div class="item-name">{{ item.name }}</div>
            <div class="item-wholesaler" v-if="item.wholesalerName">🏪 {{ item.wholesalerName }}</div>
            <div class="item-price">S$ {{ (item.price || 0).toFixed(4) }} x {{ item.quantity }}</div>
            <div class="item-tax" v-if="item.taxRate && item.taxRate > 0">
              📋 税率 {{ (item.taxRate * 100).toFixed(1) }}%
            </div>
            <div class="item-tax item-tax-free" v-else-if="item.taxRate === 0">
              📋 {{ $t('order.taxFree') }}
            </div>
          </div>
          <div class="item-actions">
            <button @click="decreaseQty(item)">-</button>
            <span>{{ item.quantity }}</span>
            <button @click="increaseQty(item)">+</button>
            <button class="btn-delete-item" @click="deleteItem(item)" :title="$t('cart.remove')">
              🗑️
            </button>
          </div>
        </div>

        <!-- 金额汇总 -->
        <div class="cart-summary">
          <div class="summary-row">
            <span>{{ $t('cart.subtotal') }}:</span>
            <span>S$ {{ selectedSubtotal.toFixed(2) }}</span>
          </div>
          <div class="summary-row">
            <span>GST / {{ $t('payment.tax') }}:</span>
            <span>S$ {{ selectedGst.toFixed(2) }}</span>
          </div>
          <div class="summary-row grand-total">
            <span>{{ $t('cart.total') }}:</span>
            <span>S$ {{ selectedTotalWithGst.toFixed(2) }}</span>
          </div>
          <div v-if="selectedIds.length < cartItems.length" class="summary-note">
            {{ $t('cart.selectedOnly') || '* Only selected items' }}
          </div>
        </div>

        <!-- 结算按钮 -->
        <div class="checkout-bar">
          <button class="btn-checkout" @click="checkout" :disabled="selectedIds.length === 0">
            {{ $t('cart.checkout') }}
            {{ selectedIds.length > 0 ? ` (${selectedIds.length})` : '' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('/stall/products')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('navigation.home') }}</span>
      </button>
      <button class="nav-item active" @click="navigateTo('/stall/cart')">
        <span class="nav-item-icon">🛒</span>
        <span>{{ $t('navigation.cart') }}</span>
        <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('/stall/orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('navigation.orders') }}</span>
        <span v-if="pendingOrders > 0" class="cart-badge">{{ pendingOrders }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('/stall/profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('navigation.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'StallCart',
  data() {
    return {
      lang: 'zh',
      cart: this.loadCartFromLocalStorage(),
      selectedIds: [],
      selectAll: false,
      packageToken: null,
      packageInfo: null,
      packageLoading: false
    };
  },
  computed: {
    cartItems() {
      return this.cart.filter(item => item.quantity > 0);
    },
    cartCount() {
      return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    },
    pendingOrders() {
      return 0;
    },
    selectedItems() {
      return this.cartItems.filter(item => this.selectedIds.includes(item.id));
    },
    selectedSubtotal() {
      return this.selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    },
    selectedGst() {
      return this.selectedItems.reduce((sum, item) => {
        const taxRate = item.taxRate || 0;
        return sum + (item.price * item.quantity * taxRate);
      }, 0);
    },
    selectedTotalWithGst() {
      return this.selectedSubtotal + this.selectedGst;
    }
  },
  mounted() {
    // 检查是否有 token/package_token 参数（扫码打开批发价包）
    this.packageToken = this.$route.query.package_token || this.$route.query.token || null;
    if (this.packageToken) {
      this.loadPackageCart();
    } else {
      // 默认全选
      this.selectedIds = this.cartItems.map(item => item.id);
      this.selectAll = true;
    }
  },
  methods: {
    navigateTo(route) {
      if (this.$route.path !== route) {
        this.$router.push(route).catch(() => {});
      }
    },
    loadCartFromLocalStorage() {
      try {
        const cartStr = localStorage.getItem('stall_cart');
        return cartStr ? JSON.parse(cartStr) : [];
      } catch (e) {
        console.error('加载购物车失败:', e);
        return [];
      }
    },
    saveCartToLocalStorage() {
      try {
        localStorage.setItem('stall_cart', JSON.stringify(this.cart));
      } catch (e) {
        console.error('保存购物车失败:', e);
      }
    },
    increaseQty(item) {
      item.quantity += 1;
      this.saveCartToLocalStorage();
    },
    decreaseQty(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        this.deleteItem(item);
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedIds = this.cartItems.map(item => item.id);
      } else {
        this.selectedIds = [];
      }
    },
    async deleteSelected() {
      if (this.selectedIds.length === 0) return;
      const confirmed = confirm(`${this.$t('toast.confirmAction')} (${this.selectedIds.length})`);
      if (confirmed) {
        this.cart = this.cart.filter(item => !this.selectedIds.includes(item.id));
        this.selectedIds = [];
        this.selectAll = false;
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('cart.deleted'));
      }
    },
    async deleteItem(item) {
      const confirmed = confirm(`${this.$t('toast.confirmAction')} (${item.name})`);
      if (confirmed) {
        this.cart = this.cart.filter(i => i.id !== item.id);
        this.selectedIds = this.selectedIds.filter(id => id !== item.id);
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('cart.itemDeleted'));
      }
    },
    async clearCart() {
      if (this.cartItems.length === 0) return;
      const confirmed = confirm(this.$t('toast.clearCart'));
      if (confirmed) {
        this.cart = [];
        this.selectedIds = [];
        this.selectAll = false;
        this.saveCartToLocalStorage();
        this.$toast?.success(this.$t('cart.cartCleared'));
      }
    },
    checkout() {
      if (this.selectedIds.length === 0) {
        this.$toast?.warning(this.$t('cart.selectItemsCheckout') || 'Please select items to checkout');
        return;
      }
      const query = {
        items: JSON.stringify(this.selectedItems)
      };
      // 如果是批发价包模式，带上 token 和 packageId
      if (this.packageToken && this.packageInfo) {
        query.packageToken = this.packageToken;
        query.packageId = this.packageInfo.id;
      }
      this.$router.push({
        path: '/customer/checkout',
        query: query
      });
    },

    // ⭐ 加载批发价包购物车
    async loadPackageCart() {
      this.packageLoading = true;
      try {
        // 1. 检查登录
        const token = localStorage.getItem('auth_token');
        if (!token) {
          this.$router.replace('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
          return;
        }

        // 2. 调公开接口获取批发价包
        const res = await api.get(`/stall/package/${this.packageToken}`);
        const pkg = res.data || res;

        this.packageInfo = {
          id: pkg.id,
          name: pkg.packageName || '',
          validDate: (pkg.validStartDate && pkg.validEndDate)
            ? `${this.formatDate(pkg.validStartDate)} 至 ${this.formatDate(pkg.validEndDate)}`
            : ''
        };

        // 3. 验证档口权限
        const customerId = localStorage.getItem('stored_customer_id');
        if (!customerId) {
          this.$toast?.error(this.$t('stall.selectStallIdentity') || '请先登录档口账号');
          this.$router.replace('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
          return;
        }
        const accessRes = await api.get(`/customer/package/${pkg.id}/access`, {
          params: { customerId }
        });
        if (!accessRes.canAccess) {
          alert('⚠️ 你无权访问此批发价包\n\n可能原因：\n1. 该批发价包未关联到你的档口账号\n2. 批发价包已过期\n3. 批发商已取消授权\n\n如有疑问请联系批发商。');
          this.$toast?.error(this.$t('cart.noPackageAccess') || '你无权访问此批发价包');
          this.$router.push('/stall/products');
          return;
        }

        // 4. 以批发价累加到购物车（不覆盖原有商品）
        if (pkg.products && pkg.products.length > 0) {
          pkg.products.forEach(p => {
            const existingItem = this.cart.find(i => i.id === (p.productId || p.id));
            if (existingItem) {
              // 已存在，更新为批发价，数量不变
              existingItem.price = p.wholesalePrice;
              existingItem.marketPrice = p.marketPrice;
            } else {
              // 不存在，追加到购物车
              this.cart.push({
                id: p.productId || p.id,
                name: p.name,
                price: p.wholesalePrice,
                marketPrice: p.marketPrice,
                quantity: 1,
                wholesalerName: '',
                taxRate: 0
              });
            }
          });
          this.saveCartToLocalStorage();
          this.selectedIds = this.cartItems.map(item => item.id);
          this.selectAll = true;
        }
      } catch (error) {
        console.error('加载批发价包失败:', error);
        if (error.response && error.response.status === 404) {
          alert('❌ 批发价包不存在或已过期\n\n该批发价包可能已被删除或超出有效期。');
          this.$toast?.error(this.$t('cart.packageNotFound') || 'Wholesale package not found or expired');
        } else if (error.response && error.response.status === 403) {
          alert('🔒 无权访问此批发价包\n\n可能原因：\n1. 该批发价包未关联到你的档口账号\n2. 批发价包已过期\n3. 批发商已取消授权\n\n如有疑问请联系批发商。');
          this.$toast?.error(this.$t('cart.noPackageAccess') || 'You do not have access to this wholesale package');
        } else {
          alert('⚠️ 加载批发价包失败\n\n错误信息：' + (error.message || '未知错误') + '\n\n请稍后重试。');
          this.$toast?.error(this.$t('toast.loadFailed') + ': ' + (error.message || 'Unknown error'));
        }
        this.$router.push('/stall/home');
      } finally {
        this.packageLoading = false;
      }
    },

    // ⭐ 退出批发价包模式
    exitPackageMode() {
      this.cart = [];
      this.packageToken = null;
      this.packageInfo = null;
      this.saveCartToLocalStorage();
      this.$router.replace('/stall/cart');
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
  }
};
</script>

<style scoped>
.stall-cart {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 60px;
}

/* ==================== 顶部导航 ==================== */
.header {
  background: white;
  border-bottom: 1px solid #eee;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-center {
  flex: 1;
  text-align: center;
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.btn-icon {
  font-size: 20px;
  cursor: pointer;
  background: none;
  border: none;
  color: #333;
}

/* ==================== 内容区域 ==================== */
.content {
  padding: 12px 16px;
}

/* ==================== 批发价包信息栏 ==================== */
.package-info-bar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 12px;
  position: relative;
}

.package-name {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.package-valid {
  font-size: 12px;
  opacity: 0.85;
}

.btn-exit-package {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255,255,255,0.25);
  color: white;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-exit-package:hover {
  background: rgba(255,255,255,0.4);
}

/* ==================== 加载中 ==================== */
.loading-cart {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.spinner {
  font-size: 32px;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ==================== 空购物车 ==================== */
.empty-cart {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-cart p {
  font-size: 16px;
  margin-bottom: 24px;
}

.btn-go-shopping {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

/* ==================== 批量操作栏 ==================== */
.batch-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.select-all-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.select-all-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.batch-buttons {
  display: flex;
  gap: 8px;
}

.btn-delete-batch,
.btn-clear-cart {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-batch {
  background: #fed7d7;
  color: #c53030;
}

.btn-delete-batch:hover:not(:disabled) {
  background: #fc8181;
}

.btn-delete-batch:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-clear-cart {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-clear-cart:hover {
  background: #cbd5e0;
}

/* ==================== 购物车商品 ==================== */
.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  gap: 12px;
}

.item-checkbox {
  flex-shrink: 0;
  width: 20px;
  display: flex;
  align-items: center;
}

.item-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
  font-weight: 500;
}

.item-wholesaler {
  font-size: 12px;
  color: #667eea;
  margin-bottom: 4px;
}

.item-price {
  font-size: 13px;
  color: #999;
}

.item-tax {
  font-size: 11px;
  color: #16a34a;
  margin-top: 2px;
}

.item-tax-free {
  color: #f59e0b;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.item-actions button {
  width: 28px;
  height: 28px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}

.item-actions button:hover {
  background: #f5f5f5;
}

.btn-delete-item {
  background: #fff5f5 !important;
  border-color: #fed7d7 !important;
}

.btn-delete-item:hover {
  background: #fed7d7 !important;
}

/* ==================== 金额汇总 ==================== */
.summary-note {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  font-style: italic;
}

.cart-summary {
  background: white;
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.summary-row.grand-total {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  padding-top: 8px;
  border-top: 2px solid #f0f0f0;
  margin-top: 8px;
}

/* ==================== 结算按钮 ==================== */
.checkout-bar {
  position: sticky;
  bottom: 60px;
  padding: 12px 0;
}

.btn-checkout {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-checkout:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ==================== 底部导航 ==================== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
  position: relative;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.cart-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #f5576c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
}
</style>